import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CurrencyDollar, Hospital, IdentificationCard, Heartbeat,
  ChartBar, CaretDown, CaretUp, ShieldCheck, Info, TrendUp
} from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Skeleton loader ─── */
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

const SkeletonCard = () => (
  <div className="border border-slate-200 rounded-lg p-4 space-y-2">
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-6 w-1/2" />
    <Skeleton className="h-3 w-full" />
  </div>
);

/* ─── Metric card ─── */
const MetricCard = ({ icon: Icon, label, value, sub, color = "teal" }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 text-${color}-600`} weight="bold" />
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

/* ─── Percentile bar ─── */
const PercentileBar = ({ percentile, label }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs text-slate-500">
      <span>{label}</span>
      <span className="font-semibold text-slate-700">{percentile}th percentile</span>
    </div>
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${
          percentile >= 75 ? "bg-teal" : percentile >= 50 ? "bg-sky-400" : percentile >= 25 ? "bg-amber-400" : "bg-slate-300"
        }`}
        style={{ width: `${Math.max(percentile, 3)}%` }}
      />
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   MedicareActivityCard — standalone, placed after OIG bar
   ══════════════════════════════════════════════════════════ */
export const MedicareActivityCard = ({ npi, providerName, specialty, state }) => {
  const [medicare, setMedicare] = useState(undefined); // undefined = loading
  const [percentile, setPercentile] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/enrichment/${npi}`);
      if (!res.ok) throw new Error("fail");
      const json = await res.json();

      if (json.medicare?.has_data) {
        setMedicare(json.medicare.data);
        setEnrichLoading(false);
      } else if (json.loading) {
        // Data not ready yet, poll again
        setTimeout(fetchData, 4000);
      } else {
        setMedicare(null);
        setEnrichLoading(false);
      }
    } catch {
      setMedicare(null);
      setEnrichLoading(false);
    }
  }, [npi]);

  // Fetch percentile separately
  useEffect(() => {
    if (!specialty || !state) return;
    fetch(`${API_URL}/api/enrichment/${npi}/percentile?specialty=${encodeURIComponent(specialty)}&state=${encodeURIComponent(state)}`)
      .then(r => r.json())
      .then(d => { if (d.has_percentile) setPercentile(d); })
      .catch(() => {});
  }, [npi, specialty, state]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Still loading
  if (enrichLoading && medicare === undefined) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="medicare-card-loading">
        <div className="flex items-center gap-2 mb-4">
          <Heartbeat className="w-4 h-4 text-slate-400" weight="bold" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  // No data
  if (!medicare) return null;

  const firstName = providerName?.split(" ")[0] || "This provider";

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6" data-testid="medicare-activity-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Heartbeat className="w-4 h-4 text-teal" weight="bold" />
        <h3 className="font-semibold text-slate-800 text-sm">Medicare Activity</h3>
        {medicare.data_year > 0 && (
          <span className="text-[11px] text-slate-400 ml-auto">Data year: {medicare.data_year}</span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* 4 Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard icon={Heartbeat} label="Patients" value={`~${medicare.total_patients.toLocaleString()}`} sub="Medicare beneficiaries" />
          <MetricCard icon={ChartBar} label="Services" value={medicare.total_services.toLocaleString()} sub="total billed" />
          <MetricCard icon={CurrencyDollar} label="Charges" value={`$${(medicare.total_charges / 1000).toFixed(0)}K`} sub="submitted" />
          <MetricCard icon={CurrencyDollar} label="Payments" value={`$${(medicare.total_payments / 1000).toFixed(0)}K`} sub="Medicare paid" color="emerald" />
        </div>

        {/* Demographics line */}
        {medicare.avg_age > 0 && (
          <div className="flex gap-4 text-xs text-slate-500 px-1 flex-wrap">
            <span>Avg patient age: <strong className="text-slate-700">{medicare.avg_age}</strong></span>
            {medicare.hcpcs_codes > 0 && <span>Unique procedure codes: <strong className="text-slate-700">{medicare.hcpcs_codes}</strong></span>}
            {(medicare.female_count > 0 || medicare.male_count > 0) && (
              <span>Gender split: <strong className="text-slate-700">{medicare.female_count}F / {medicare.male_count}M</strong></span>
            )}
          </div>
        )}

        {/* Percentile Comparison */}
        {percentile && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid="percentile-comparison">
            <div className="flex items-center gap-2">
              <TrendUp className="w-4 h-4 text-teal" weight="bold" />
              <span className="text-sm font-semibold text-slate-800">Peer Comparison</span>
              <span className="text-[11px] text-slate-400 ml-auto">vs {percentile.group_size} {percentile.specialty} providers</span>
            </div>

            <p className="text-sm text-slate-700" data-testid="percentile-statement">
              <strong className="text-slate-900">{firstName}</strong> billed more Medicare than{" "}
              <strong className="text-teal">{percentile.payments_percentile}%</strong> of{" "}
              {percentile.specialty} providers{percentile.state ? ` in ${percentile.state}` : ""}.
            </p>

            <div className="space-y-2.5">
              <PercentileBar percentile={percentile.payments_percentile} label="Medicare payments" />
              <PercentileBar percentile={percentile.services_percentile} label="Services billed" />
            </div>
          </div>
        )}

        {/* Source disclaimer */}
        <div className="flex items-start gap-1.5 text-[11px] text-slate-400 px-1">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          Source: CMS Medicare Provider Utilisation and Payment Data. Does not reflect total practice volume.
        </div>
      </div>
    </div>
  );
};


/* ─── Identifiers Section ─── */
const IdentifiersSection = ({ data, loading }) => {
  if (loading) return <div className="grid grid-cols-1 gap-3"><SkeletonCard /><SkeletonCard /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="identifiers-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <IdentificationCard className="w-4 h-4 text-slate-600" weight="bold" />
        <h3 className="font-semibold text-slate-800 text-sm">Identifiers & Licenses</h3>
      </div>
      {data.length === 0 ? (
        <div className="px-5 py-4 text-sm text-slate-500">
          No additional identifiers listed in NPPES. Providers voluntarily add DEA and licence numbers to their NPI profile.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs text-slate-500 uppercase">
            <th className="px-5 py-2 text-left font-medium">Type</th>
            <th className="px-5 py-2 text-left font-medium">Number</th>
            <th className="px-5 py-2 text-left font-medium">Issuer</th>
            <th className="px-5 py-2 text-left font-medium">State</th>
          </tr></thead>
          <tbody>
            {data.map((id, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-5 py-2.5 font-medium text-slate-700">{id.type}</td>
                <td className="px-5 py-2.5 text-slate-600 font-mono text-xs">{id.value}</td>
                <td className="px-5 py-2.5 text-slate-500">{id.issuer || "—"}</td>
                <td className="px-5 py-2.5 text-slate-500">{id.state || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ─── Hospital Affiliations Section ─── */
const AffiliationsSection = ({ data, loading }) => {
  if (loading) return <div className="grid sm:grid-cols-2 gap-3"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="affiliations-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Hospital className="w-4 h-4 text-slate-600" weight="bold" />
        <h3 className="font-semibold text-slate-800 text-sm">Hospital Affiliations</h3>
      </div>
      {data.length === 0 ? (
        <div className="px-5 py-4 text-sm text-slate-500">
          No hospital affiliations on record in CMS Care Compare.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 p-4">
          {data.map((aff, i) => (
            <div key={i} className="border border-slate-150 rounded-md p-3 hover:border-teal transition-colors">
              <div className="font-medium text-slate-800 text-sm">{aff.hospital_name}</div>
              <div className="text-xs text-slate-500 mt-1">{aff.hospital_city}, {aff.hospital_state}</div>
              {aff.hospital_npi && (
                <Link to={`/provider/${aff.hospital_npi}`} className="text-xs text-teal hover:underline mt-1 inline-block">
                  View hospital NPI
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Open Payments Section ─── */
const PaymentsSection = ({ payments, loading }) => {
  const [showAll, setShowAll] = useState(false);

  if (loading) return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-24 w-full" />
    </div>
  );

  const { zero_payments, total_count, total_amount, year_range, yearly_totals, records } = payments;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="payments-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <CurrencyDollar className="w-4 h-4 text-slate-600" weight="bold" />
        <h3 className="font-semibold text-slate-800 text-sm">Industry Payments (Open Payments)</h3>
      </div>

      {zero_payments || total_count === 0 ? (
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" weight="fill" />
            <span className="text-sm font-medium text-emerald-700">No pharmaceutical payments on record</span>
          </div>
          <p className="text-xs text-slate-500">
            No payments from pharmaceutical or medical device companies were found in the CMS Open Payments database for this provider.
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {/* Summary */}
          <div className="bg-slate-50 rounded-md px-4 py-3 text-sm text-slate-700">
            This provider received <strong>{total_count}</strong> payments totalling{" "}
            <strong className="text-slate-900">${total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>{" "}
            from pharmaceutical and medical device companies
            {year_range.length > 0 && (
              <> between <strong>{year_range[0]}</strong> and <strong>{year_range[year_range.length - 1]}</strong></>
            )}.
          </div>

          {/* Year breakdown bar chart */}
          {yearly_totals && Object.keys(yearly_totals).length > 1 && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Payments by Year</div>
              {Object.entries(yearly_totals).sort(([a],[b]) => Number(a) - Number(b)).map(([year, amount]) => {
                const maxAmt = Math.max(...Object.values(yearly_totals));
                const pct = maxAmt > 0 ? (amount / maxAmt) * 100 : 0;
                return (
                  <div key={year} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-10 shrink-0">{year}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div className="bg-teal h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-20 text-right">
                      ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                <th className="px-3 py-2 text-left font-medium">Company</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-center font-medium">Year</th>
                <th className="px-3 py-2 text-left font-medium">Drug/Device</th>
              </tr></thead>
              <tbody>
                {(showAll ? records : records.slice(0, 10)).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate">{r.company_name}</td>
                    <td className="px-3 py-2 text-slate-500 max-w-[140px] truncate">{r.payment_type}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-800">
                      ${r.payment_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-500">{r.payment_year}</td>
                    <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate">{r.drug_or_device_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total_count > 10 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-1 text-sm text-teal hover:text-teal-700 font-medium mx-auto"
              data-testid="show-all-payments-btn"
            >
              {showAll ? <><CaretUp className="w-3 h-3" /> Show less</> : <><CaretDown className="w-3 h-3" /> Show all {total_count} payments</>}
            </button>
          )}

          <div className="flex items-start gap-1.5 text-[11px] text-slate-400 px-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            Source: CMS Open Payments database. Payments are disclosed by manufacturers as required by the Physician Payments Sunshine Act. Disclosure does not imply impropriety.
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   ProviderEnrichment — remaining sections (no Medicare, it moved up)
   ══════════════════════════════════════════════════════════ */
export const ProviderEnrichment = ({ npi }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEnrichment = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/enrichment/${npi}`);
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setData(json);

      if (json.loading) {
        setTimeout(fetchEnrichment, 5000);
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  }, [npi]);

  useEffect(() => {
    fetchEnrichment();
  }, [fetchEnrichment]);

  const hasAnyData = data?.has_data;
  const isLoading = loading && !hasAnyData;

  return (
    <div className="space-y-5 mt-8" data-testid="provider-enrichment">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-lg font-bold text-slate-900">Detailed Provider Data</h2>
        {data?.last_fetched && !loading && (
          <span className="text-[11px] text-slate-400">
            Data last updated {new Date(data.last_fetched).toLocaleDateString()}
          </span>
        )}
        {loading && data?.loading && (
          <span className="text-[11px] text-amber-500 flex items-center gap-1">
            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" /> Fetching from CMS...
          </span>
        )}
      </div>

      <IdentifiersSection
        data={data?.identifiers?.records || []}
        loading={isLoading || (data?.loading && !data?.identifiers?.has_data)}
      />

      <AffiliationsSection
        data={data?.affiliations?.records || []}
        loading={isLoading || (data?.loading && !data?.affiliations?.has_data)}
      />

      <PaymentsSection
        payments={data?.payments || { zero_payments: false, total_count: 0, total_amount: 0, year_range: [], yearly_totals: {}, records: [] }}
        loading={isLoading || (data?.loading && !data?.payments?.has_data)}
      />

      {/* Data sources footer */}
      {!isLoading && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-5 py-3 text-[11px] text-slate-400 leading-relaxed" data-testid="enrichment-sources">
          Data on this page is sourced from the NPPES NPI Registry (CMS), CMS Open Payments, CMS Medicare Provider Utilisation Data, and CMS Care Compare.
          All data is sourced from official US government databases and is updated periodically. This information is provided for verification purposes only.
        </div>
      )}
    </div>
  );
};
